import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { supabase } from "../utils/supabase"
import {
	Box,
	CircularProgress,
	TextField,
	MenuItem,
	Alert,
	useMediaQuery
} from '@mui/material'
import dayjs from 'dayjs'
import theme from '../theme'
import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY, getCurrencySymbol } from '../utils/currencyUtils'
import { ALL_PAYMENT_METHODS, DEFAULT_PAYMENT_METHOD_VALUES } from '../utils/paymentMethodUtils'
import { getTimeZones } from '@vvo/tzdb'
import { L } from '../components/ui/LavDropdown'
import DropdownCalendar from '../components/ui/DropdownCalendar'
import {
	StIc, StPanel, StField, StGrid2, StNote, StStatusPill, StTabs, StSaveBar,
	stInputSx, stMenuProps, stPrimaryBtnSx, stGhostBtnSx, stDangerBtnSx,
} from '../components/settings/stBits'
import SettingsReminders from '../components/settings/SettingsReminders'
import StoreHoursEditor, { StCountPill, hoursSummary } from '../components/settings/StoreHoursEditor'
import BusinessLogoUpload from '../components/settings/BusinessLogoUpload'

// Desktop "Settings · workspace" sub-tabs (Square folds into Payments per the design)
const ST_TABS = [
	{ id: 'store', label: 'Business', icon: 'building' },
	{ id: 'reminders', label: 'Reminders', icon: 'bell' },
	{ id: 'policy', label: 'Policies', icon: 'shield' },
	{ id: 'payments', label: 'Payments', icon: 'card' },
	{ id: 'messaging', label: 'Messaging', icon: 'message' },
]

// Get timezone list once - sorted by offset then name
const TIMEZONES = getTimeZones()

export default function Settings({ merchant, onSaved, hasUnsavedChangesRef }) {
	const [activeTab, setActiveTab] = useState('store') // 'general', 'store', 'reminders', 'profile'
	// Mobile navigation: 'home' shows the section menu; 'section' shows the
	// full-screen page for `activeTab`. (Desktop ignores this — it shows tabs.)
	const [mobileScreen, setMobileScreen] = useState('home')
	const [saving, setSaving] = useState(false)
	const [errorMessage, setErrorMessage] = useState("")
	const [successMessage, setSuccessMessage] = useState("")

	// Reminders state
	const [reminderData, setReminderData] = useState(null)
	const remindersCancelRef = useRef(null)
	const remindersSaveSuccessRef = useRef(null)
	// Use parent's ref if provided, otherwise create a local one
	// Reminders track their own dirty state in this local ref; Settings owns the
	// parent `hasUnsavedChangesRef` exclusively and syncs the combined
	// "any unsaved changes" state into it (see the effect below), so the
	// navigate-away warning fires for edits on ANY tab, not just reminders.
	const localRemindersHasUnsavedChangesRef = useRef(false)
	const remindersHasUnsavedChangesRef = localRemindersHasUnsavedChangesRef

	// Store tab - Business details
	const [name, setName] = useState(merchant?.name || "")
	const [timezone, setTimezone] = useState(merchant?.timezone || 'America/Los_Angeles')
	const [currency, setCurrency] = useState(merchant?.currency || DEFAULT_CURRENCY)
	const [phone, setPhone] = useState(merchant?.phone || "")
	const [taxRate, setTaxRate] = useState(merchant?.tax_rate || '')


	// Store tab - Hours
	const emptyHours = useMemo(() => ({ "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] }), [])
	const [weeklyHours, setWeeklyHours] = useState(merchant?.weekly_hours || emptyHours)

	// Store tab - Closed dates
	const [closedDates, setClosedDates] = useState(merchant?.closed_recurring_dates || [])
	// Closed dates are picked as a range (start → end, per the design's
	// `st-closeddates` row). A single day is just start === end.
	const [tempClosedStart, setTempClosedStart] = useState(null)
	const [tempClosedEnd, setTempClosedEnd] = useState(null)

	// Policy tab - Fee policy
	const [lateCancelHours, setLateCancelHours] = useState(merchant?.late_cancel_hours || '')
	const [lateCancelHoursCustom, setLateCancelHoursCustom] = useState(false)
	const [lateCancelFeeType, setLateCancelFeeType] = useState(merchant?.late_cancel_fee_type || '')
	const [lateCancelFeeValue, setLateCancelFeeValue] = useState(merchant?.late_cancel_fee_value || '')
	const [lateCancelFeeCustom, setLateCancelFeeCustom] = useState(false)
	const [noShowFeeType, setNoShowFeeType] = useState(merchant?.no_show_fee_type || '')
	const [noShowFeeValue, setNoShowFeeValue] = useState(merchant?.no_show_fee_value || '')
	const [noShowFeeCustom, setNoShowFeeCustom] = useState(false)

	// Square integration tab
	const isDev = process.env.REACT_APP_ENV === 'development'
	const squareAppId = isDev
		? (process.env.REACT_APP_SQUARE_APP_ID_DEV || '')
		: (process.env.REACT_APP_SQUARE_APP_ID || '')
	const squareBaseUrl = isDev
		? 'https://connect.squareupsandbox.com'
		: 'https://connect.squareup.com'
	const [squareDisconnecting, setSquareDisconnecting] = useState(false)
	const isSquareConnected = Boolean(merchant?.square_access_token)

	// Messaging (Twilio) tab
	const [messagingAreaCode, setMessagingAreaCode] = useState('')
	const [messagingProvisioning, setMessagingProvisioning] = useState(false)
	const [messagingReleasing, setMessagingReleasing] = useState(false)
	const [messagingError, setMessagingError] = useState('')
	const [messagingSuccess, setMessagingSuccess] = useState('')
	// Number-picker state: search results, loading, selection.
	const [messagingAvailableNumbers, setMessagingAvailableNumbers] = useState([])
	const [messagingSearching, setMessagingSearching] = useState(false)
	const [messagingHasSearched, setMessagingHasSearched] = useState(false)
	const [selectedMessagingNumber, setSelectedMessagingNumber] = useState('')
	const isMessagingEnabled = Boolean(merchant?.twilio_phone_number)
	// Admin-controlled feature flags. Both default to false in the DB, so a
	// merchant cannot purchase or release a number until support grants access.
	const canPurchaseMessagingNumber = Boolean(merchant?.twilio_allow_messaging)
	const canReleaseMessagingNumber = Boolean(merchant?.twilio_allow_release)

	// Terminal device selection
	const [squareDevices, setSquareDevices] = useState([])
	const [squareDevicesLoading, setSquareDevicesLoading] = useState(false)
	const [squareDevicesError, setSquareDevicesError] = useState('')
	const [selectedTerminalDeviceId, setSelectedTerminalDeviceId] = useState(merchant?.square_terminal_device_id || '')
	const [savingTerminalDevice, setSavingTerminalDevice] = useState(false)
	const [terminalDeviceSaved, setTerminalDeviceSaved] = useState(false)
	// Show picker only when no device is saved yet, or when merchant explicitly wants to change
	const [showDevicePicker, setShowDevicePicker] = useState(false)

	const loadSquareDevices = useCallback(async () => {
		if (!merchant?.id) return
		setSquareDevicesLoading(true)
		setSquareDevicesError('')
		try {
			const { data, error } = await supabase.functions.invoke('square-list-devices', {
				body: { merchantId: merchant.id },
			})
			if (error || data?.error) {
				setSquareDevicesError(data?.error || error?.message || 'Failed to load terminal devices.')
			} else {
				setSquareDevices(data?.devices || [])
			}
		} catch (err) {
			setSquareDevicesError(err.message || 'Unexpected error loading devices.')
		} finally {
			setSquareDevicesLoading(false)
		}
	}, [merchant?.id])

	const handleOpenDevicePicker = () => {
		setShowDevicePicker(true)
		setSelectedTerminalDeviceId(merchant?.square_terminal_device_id || '')
		loadSquareDevices()
	}

	const handleCancelDevicePicker = () => {
		setShowDevicePicker(false)
		setSquareDevices([])
		setSquareDevicesError('')
		setSelectedTerminalDeviceId(merchant?.square_terminal_device_id || '')
	}

	const handleSaveTerminalDevice = async () => {
		setSavingTerminalDevice(true)
		setTerminalDeviceSaved(false)
		const { data, error } = await supabase
			.from('merchant')
			.update({ square_terminal_device_id: selectedTerminalDeviceId || null })
			.eq('id', merchant?.id)
			.select()
			.single()
		setSavingTerminalDevice(false)
		if (error) {
			setErrorMessage('Failed to save terminal device selection.')
		} else {
			setTerminalDeviceSaved(true)
			setShowDevicePicker(false)
			onSaved && onSaved(data)
			setTimeout(() => setTerminalDeviceSaved(false), 3000)
		}
	}

	// Terminal pairing via a Terminal API device code.
	// Square only lists a Terminal for our app once it has signed in with a code we created,
	// so a Terminal logged in with the merchant's normal Square account never appears in the
	// picker above until it has gone through this flow.
	const [pairing, setPairing] = useState(null) // { deviceCodeId, code, pairBy }
	const [pairingStatus, setPairingStatus] = useState('idle') // idle | creating | waiting | saving | paired | expired | error
	const [pairingError, setPairingError] = useState('')
	const pairingPollRef = useRef(null)

	const stopPairingPoll = useCallback(() => {
		if (pairingPollRef.current) {
			clearInterval(pairingPollRef.current)
			pairingPollRef.current = null
		}
	}, [])

	useEffect(() => () => stopPairingPoll(), [stopPairingPoll])

	const handleCancelPairing = () => {
		stopPairingPoll()
		setPairing(null)
		setPairingStatus('idle')
		setPairingError('')
	}

	const checkPairingStatus = async (deviceCodeId, pairBy) => {
		try {
			const { data, error } = await supabase.functions.invoke('square-device-code', {
				body: { action: 'status', merchantId: merchant?.id, deviceCodeId },
			})
			if (error || data?.error) return // transient; keep polling until the code expires

			if (data?.status === 'PAIRED' && data?.deviceId) {
				stopPairingPoll()
				setPairingStatus('saving')
				const { data: saved, error: saveError } = await supabase
					.from('merchant')
					.update({ square_terminal_device_id: data.deviceId })
					.eq('id', merchant?.id)
					.select()
					.single()
				if (saveError) {
					setPairingStatus('error')
					setPairingError('Terminal paired, but saving the device failed. Use "Select device" to pick it.')
					return
				}
				setPairing(null)
				setPairingStatus('paired')
				setTerminalDeviceSaved(true)
				onSaved && onSaved(saved)
				setTimeout(() => {
					setTerminalDeviceSaved(false)
					setPairingStatus('idle')
				}, 4000)
				return
			}

			const expiresAt = data?.pairBy || pairBy
			const expired = data?.status === 'EXPIRED' || (expiresAt && new Date(expiresAt).getTime() < Date.now())
			if (expired) {
				stopPairingPoll()
				setPairingStatus('expired')
			}
		} catch (_) {
			// ignore transient network errors; next tick retries
		}
	}

	const handleGeneratePairingCode = async () => {
		if (!merchant?.id) return
		stopPairingPoll()
		setPairing(null)
		setPairingError('')
		setPairingStatus('creating')
		try {
			const { data, error } = await supabase.functions.invoke('square-device-code', {
				body: { action: 'create', merchantId: merchant.id },
			})
			if (error || data?.error || !data?.code) {
				setPairingStatus('error')
				setPairingError(data?.error || error?.message || 'Failed to create a pairing code.')
				return
			}
			setPairing({ deviceCodeId: data.deviceCodeId, code: data.code, pairBy: data.pairBy })
			setPairingStatus('waiting')
			pairingPollRef.current = setInterval(() => checkPairingStatus(data.deviceCodeId, data.pairBy), 4000)
		} catch (err) {
			setPairingStatus('error')
			setPairingError(err.message || 'Unexpected error creating a pairing code.')
		}
	}

	const handleConnectSquare = () => {
		if (!squareAppId) {
			alert('Square App ID is not configured. Please set REACT_APP_SQUARE_APP_ID in your environment.')
			return
		}
		const redirectUri = `${window.location.origin}/oauth/square/callback`
		const scope = 'MERCHANT_PROFILE_READ PAYMENTS_WRITE PAYMENTS_READ ORDERS_WRITE ORDERS_READ DEVICE_CREDENTIAL_MANAGEMENT DEVICES_READ CUSTOMERS_WRITE CUSTOMERS_READ'
		const url = `${squareBaseUrl}/oauth2/authorize?client_id=${squareAppId}&scope=${encodeURIComponent(scope)}&session=false&state=${merchant?.id}&redirect_uri=${encodeURIComponent(redirectUri)}`
		window.location.href = url
	}

	const handleDisconnectSquare = async () => {
		if (!window.confirm('Disconnect Square? This will remove your Square access tokens and terminal device selection.')) return
		setSquareDisconnecting(true)

		const updatePayload = {
			square_access_token: null,
			square_refresh_token: null,
			square_merchant_id: null,
			square_location_id: null,
			square_token_expires_at: null,
			square_terminal_device_id: null,
		}

		// Strip any Square-backed methods from the saved list — they require an active connection.
		if (merchant?.payment_methods?.some(m => m === 'square_terminal' || m === 'square_card')) {
			const updated = merchant.payment_methods.filter(m => m !== 'square_terminal' && m !== 'square_card')
			updatePayload.payment_methods = updated.length > 0 ? updated : null
		}

		const { error } = await supabase
			.from('merchant')
			.update(updatePayload)
			.eq('id', merchant?.id)
		setSquareDisconnecting(false)
		if (error) {
			setErrorMessage('Failed to disconnect Square.')
		} else {
			window.location.reload()
		}
	}

	// ── Messaging (Twilio) handlers ─────────────────────────────────────────────
	// Step 1: search for available numbers (no purchase yet).
	const handleSearchTwilioNumbers = async () => {
		if (!merchant?.id) return
		const trimmedAreaCode = messagingAreaCode.trim()
		if (trimmedAreaCode && !/^\d{3}$/.test(trimmedAreaCode)) {
			setMessagingError('Area code must be exactly 3 digits, or left blank for any.')
			return
		}
		setMessagingSearching(true)
		setMessagingError('')
		setMessagingSuccess('')
		setSelectedMessagingNumber('')
		setMessagingAvailableNumbers([])
		try {
			const { data, error } = await supabase.functions.invoke('twilio-list-available-numbers', {
				body: {
					merchantId: merchant.id,
					...(trimmedAreaCode ? { areaCode: trimmedAreaCode } : {}),
				},
			})
			if (error || data?.error) {
				let msg = data?.error || error?.message || 'Failed to fetch numbers.'
				if (data?.detail) msg += ` (${data.detail})`
				throw new Error(msg)
			}
			setMessagingAvailableNumbers(data?.numbers || [])
		} catch (err) {
			setMessagingError(err.message || 'Unexpected error searching for numbers.')
		} finally {
			setMessagingHasSearched(true)
			setMessagingSearching(false)
		}
	}

	// Step 2: purchase the user-selected number.
	const handlePurchaseSelectedNumber = async () => {
		if (!merchant?.id || !selectedMessagingNumber) return

		const confirmed = window.confirm(
			`Purchase ${selectedMessagingNumber}?\n\n` +
			'This will rent the number from Twilio for approximately $1.15 USD/month, ' +
			'billed to your Twilio account. You can release it at any time.'
		)
		if (!confirmed) return

		setMessagingProvisioning(true)
		setMessagingError('')
		setMessagingSuccess('')
		try {
			const { data, error } = await supabase.functions.invoke('twilio-provision-number', {
				body: {
					merchantId: merchant.id,
					phoneNumber: selectedMessagingNumber,
				},
			})
			if (error || data?.error) {
				let msg = data?.error || error?.message || 'Failed to purchase number.'
				if (data?.detail) msg += ` (${data.detail})`
				throw new Error(msg)
			}
			setMessagingSuccess(`Number purchased: ${data.phoneNumber}`)
			setTimeout(() => window.location.reload(), 800)
		} catch (err) {
			setMessagingError(err.message || 'Unexpected error purchasing number.')
		} finally {
			setMessagingProvisioning(false)
		}
	}

	const handleReleaseTwilioNumber = async () => {
		if (!merchant?.id) return
		const confirmed = window.confirm(
			'Release your messaging number?\n\n' +
			'• You will stop being billed by Twilio.\n' +
			'• Clients will no longer be able to text you at this number.\n' +
			'• Existing conversations remain in your inbox, but you can no longer reply.\n\n' +
			'This cannot be undone — re-enabling messaging will assign you a different number.'
		)
		if (!confirmed) return

		setMessagingReleasing(true)
		setMessagingError('')
		setMessagingSuccess('')
		try {
			const { data, error } = await supabase.functions.invoke('twilio-release-number', {
				body: { merchantId: merchant.id },
			})
			if (error || data?.error) {
				let msg = data?.error || error?.message || 'Failed to release number.'
				if (data?.detail) msg += ` (${data.detail})`
				throw new Error(msg)
			}
			setMessagingSuccess('Number released.')
			setTimeout(() => window.location.reload(), 800)
		} catch (err) {
			setMessagingError(err.message || 'Unexpected error releasing number.')
		} finally {
			setMessagingReleasing(false)
		}
	}

	// Payment methods tab
	const resolvePaymentMethods = (m) =>
		m?.payment_methods && m.payment_methods.length > 0
			? m.payment_methods
			: DEFAULT_PAYMENT_METHOD_VALUES

	const [paymentMethods, setPaymentMethods] = useState(() => resolvePaymentMethods(merchant))
	// Baseline: what is currently saved in DB (used to detect unsaved changes)
	const [savedPaymentMethods, setSavedPaymentMethods] = useState(() => resolvePaymentMethods(merchant))

	// True when current selection differs from the saved baseline
	const paymentMethodsHasUnsaved = useMemo(() => {
		const a = [...paymentMethods].sort()
		const b = [...savedPaymentMethods].sort()
		return JSON.stringify(a) !== JSON.stringify(b)
	}, [paymentMethods, savedPaymentMethods])

	const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Handle sub-tab changes. Consistent across every tab: if the tab being left
	// has unsaved changes, warn — and on confirm, discard those edits before
	// switching (matches the page-close + leave-Settings warnings, which also
	// cover all tabs via `anyUnsavedChanges`). Plain function (not memoized) so
	// it always sees the latest dirty flags / discardChanges.
	// (storeDirty/policyDirty/discardChanges are defined just below; this only
	// reads them when invoked on a click, so their later declaration is fine.)
	const handleTabChange = (newTab) => {
		if (newTab === activeTab) return

		const currentTabDirty = {
			store: storeDirty,
			policy: policyDirty,
			payments: paymentMethodsHasUnsaved,
			reminders: Boolean(remindersHasUnsavedChangesRef.current),
		}[activeTab]

		if (currentTabDirty) {
			const confirmLeave = window.confirm(
				'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
			)
			if (!confirmLeave) return
			// Confirmed — discard the unsaved edits before switching tabs.
			discardChanges()
		}

		setActiveTab(newTab)
	}

	// Per-tab unsaved-changes detection. Each field is compared against the exact
	// value it's initialized to from `merchant` (see the merchant effect below),
	// so a freshly loaded, untouched tab is never dirty. Drives both the sticky
	// save bar and the navigate-away warning.
	const jsonEq = (a, b) => JSON.stringify(a) === JSON.stringify(b)
	const storeDirty =
		name !== (merchant?.name || '') ||
		timezone !== (merchant?.timezone || 'America/Los_Angeles') ||
		currency !== (merchant?.currency || DEFAULT_CURRENCY) ||
		phone !== (merchant?.phone || '') ||
		String(taxRate) !== String(merchant?.tax_rate || '') ||
		!jsonEq(weeklyHours, merchant?.weekly_hours || emptyHours) ||
		!jsonEq(closedDates, merchant?.closed_recurring_dates || [])
	const policyDirty =
		lateCancelHours !== (merchant?.late_cancel_hours != null ? String(merchant.late_cancel_hours) : '') ||
		lateCancelFeeType !== (merchant?.late_cancel_fee_type || '') ||
		String(lateCancelFeeValue) !== String(merchant?.late_cancel_fee_value || '') ||
		noShowFeeType !== (merchant?.no_show_fee_type || '') ||
		String(noShowFeeValue) !== String(merchant?.no_show_fee_value || '')
	// The reminders child updates its local ref alongside onChange(setReminderData),
	// which re-renders Settings — so reading the ref here stays in sync.
	const anyUnsavedChanges =
		storeDirty || policyDirty || paymentMethodsHasUnsaved ||
		Boolean(remindersHasUnsavedChangesRef.current)

	// Propagate combined dirty state to the parent ref so the "leave settings?"
	// navigation warning fires for unsaved changes on any tab. Always mirror the
	// current value so the ref clears after a successful save.
	useEffect(() => {
		if (hasUnsavedChangesRef) {
			hasUnsavedChangesRef.current = anyUnsavedChanges
		}
	}, [anyUnsavedChanges, hasUnsavedChangesRef])

	// Warn on browser/tab close when there are unsaved changes on any tab.
	useEffect(() => {
		const handler = (e) => {
			if (anyUnsavedChanges) {
				e.preventDefault()
				e.returnValue = ''
			}
		}
		window.addEventListener('beforeunload', handler)
		return () => window.removeEventListener('beforeunload', handler)
	}, [anyUnsavedChanges])

	useEffect(() => {
		if (!merchant) return

		setName(merchant?.name || "")
		setTimezone(merchant?.timezone || 'America/Los_Angeles')
		setCurrency(merchant?.currency || DEFAULT_CURRENCY)
		setPhone(merchant?.phone || "")
		setTaxRate(merchant?.tax_rate || '')

		// Initialize fee policy fields
		const hours = merchant?.late_cancel_hours != null ? String(merchant.late_cancel_hours) : ''
		const hoursIsCustom = hours !== '' && hours !== '24' && hours !== '48' && hours !== '72'
		setLateCancelHours(hours)
		setLateCancelHoursCustom(hoursIsCustom)

		setLateCancelFeeType(merchant?.late_cancel_fee_type || '')
		const lateCancelValue = merchant?.late_cancel_fee_value || ''
		const lateCancelIsCustom = lateCancelValue !== '' &&
			(merchant?.late_cancel_fee_type === 'percentage' ?
				(lateCancelValue !== 25 && lateCancelValue !== 50) :
				(lateCancelValue !== 1000 && lateCancelValue !== 2500))
		setLateCancelFeeValue(lateCancelValue)
		setLateCancelFeeCustom(lateCancelIsCustom)

		setNoShowFeeType(merchant?.no_show_fee_type || '')
		const noShowValue = merchant?.no_show_fee_value || ''
		const noShowIsCustom = noShowValue !== '' &&
			(merchant?.no_show_fee_type === 'percentage' ?
				(noShowValue !== 50 && noShowValue !== 100) :
				(noShowValue !== 2500 && noShowValue !== 5000))
		setNoShowFeeValue(noShowValue)
		setNoShowFeeCustom(noShowIsCustom)

		setWeeklyHours(merchant?.weekly_hours || emptyHours)
		setClosedDates(merchant?.closed_recurring_dates || [])
		const resolved = resolvePaymentMethods(merchant)
		setPaymentMethods(resolved)
		setSavedPaymentMethods(resolved)
	}, [merchant, emptyHours])

	// Discard all unsaved changes — revert every field back to the last-saved
	// merchant values (same initialization the merchant effect applies), clear
	// any in-progress hour/date inputs, and let the reminders child reset itself.
	const discardChanges = () => {
		if (!merchant) return

		// Store profile
		setName(merchant?.name || '')
		setTimezone(merchant?.timezone || 'America/Los_Angeles')
		setCurrency(merchant?.currency || DEFAULT_CURRENCY)
		setPhone(merchant?.phone || '')
		setTaxRate(merchant?.tax_rate || '')

		// Store hours + closed dates (and the "open day" flags + pending inputs)
		setWeeklyHours(merchant?.weekly_hours || emptyHours)
		setClosedDates(merchant?.closed_recurring_dates || [])
		const openReset = {}
		for (let i = 0; i < 7; i++) openReset[String(i)] = (merchant?.weekly_hours?.[String(i)] || []).length > 0
		setOpenDays(openReset)
		setTempClosedStart(null)
		setTempClosedEnd(null)

		// Policy (mirror the custom-flag derivation from the merchant effect)
		const hours = merchant?.late_cancel_hours != null ? String(merchant.late_cancel_hours) : ''
		setLateCancelHours(hours)
		setLateCancelHoursCustom(hours !== '' && hours !== '24' && hours !== '48' && hours !== '72')
		setLateCancelFeeType(merchant?.late_cancel_fee_type || '')
		const lcv = merchant?.late_cancel_fee_value || ''
		setLateCancelFeeValue(lcv)
		setLateCancelFeeCustom(lcv !== '' && (merchant?.late_cancel_fee_type === 'percentage'
			? (lcv !== 25 && lcv !== 50)
			: (lcv !== 1000 && lcv !== 2500)))
		setNoShowFeeType(merchant?.no_show_fee_type || '')
		const nsv = merchant?.no_show_fee_value || ''
		setNoShowFeeValue(nsv)
		setNoShowFeeCustom(nsv !== '' && (merchant?.no_show_fee_type === 'percentage'
			? (nsv !== 50 && nsv !== 100)
			: (nsv !== 2500 && nsv !== 5000)))

		// Payment methods → back to saved baseline
		setPaymentMethods(savedPaymentMethods)

		// Reminders manage their own state in the child (only mounted on that
		// tab); ask it to reset when it's the active tab.
		if (activeTab === 'reminders' && remindersCancelRef.current) remindersCancelRef.current()

		setSuccessMessage('')
		setErrorMessage('')
	}

	// Track which days are marked as "open" even if they have no intervals yet
	const [openDays, setOpenDays] = useState(() => {
		const initial = {}
		for (let i = 0; i < 7; i++) {
			initial[String(i)] = (merchant?.weekly_hours?.[String(i)] || []).length > 0
		}
		return initial
	})

	// Sync openDays when merchant changes
	useEffect(() => {
		if (!merchant) return
		const updated = {}
		for (let i = 0; i < 7; i++) {
			updated[String(i)] = (merchant.weekly_hours?.[String(i)] || []).length > 0
		}
		setOpenDays(updated)
	}, [merchant])

	const isDayOpen = (dayKey) => openDays[dayKey] ?? false
	const setDayOpen = (dayKey, open) => setOpenDays(prev => ({ ...prev, [dayKey]: open }))
	// "N days open · H hrs / week" pills on the Store hours panel header.
	const hoursPills = hoursSummary(weeklyHours, isDayOpen)

	// Closed dates are stored as recurring 'MM-DD' strings (matched by
	// isDateClosed() and by the merchant availability SQL function), so a picked
	// range is expanded into one entry per day. Ranges may cross a year boundary
	// (e.g. Dec 28 → Jan 2), which falls out of iterating real dates.
	const addClosedDate = () => {
		if (!tempClosedStart) return
		const start = dayjs(tempClosedStart)
		const end = tempClosedEnd ? dayjs(tempClosedEnd) : start
		if (end.isBefore(start, 'day')) return

		const span = end.diff(start, 'day') + 1
		if (span > 366) {
			setErrorMessage('That range covers more than a year — pick a shorter one.')
			return
		}

		setClosedDates((prev) => {
			const next = [...prev]
			for (let i = 0; i < span; i++) {
				const mmdd = start.add(i, 'day').format('MM-DD')
				if (!next.includes(mmdd)) next.push(mmdd)
			}
			return next
		})
		setTempClosedStart(null)
		setTempClosedEnd(null)
	}

	const removeClosedDate = (mmdd) => {
		setClosedDates((prev) => prev.filter((d) => d !== mmdd))
	}

	const handleSave = async () => {
		setSaving(true)
		setErrorMessage("")
		setSuccessMessage("")

		try {
			let payload = {}

			if (activeTab === 'store') {
				payload = {
					name: name.trim(),
					timezone,
					currency,
					phone: phone.trim() || null,
					tax_rate: taxRate === '' || taxRate === null ? null : Number(taxRate),
					weekly_hours: weeklyHours,
					// While the landing page inherits store hours (inherited_hours is
					// true/unset), keep the display copy mirrored so the two columns
					// never drift. Custom landing-page hours (inherited_hours === false)
					// are intentionally different and stay untouched.
					...(merchant?.inherited_hours !== false ? { weekly_hours_display: weeklyHours } : {}),
					closed_recurring_dates: closedDates,
				}
			} else if (activeTab === 'reminders') {
				if (reminderData) {
					// Helper to normalize email body: convert null/undefined/<p></p> to empty string
					const normalizeEmailBody = (body) => {
						if (!body) return "" // null, undefined, or empty string → ""
						const trimmed = body.trim()
						return (trimmed === '' || trimmed === '<p></p>') ? '' : body
					}

					// Helper to normalize text: convert null/undefined/whitespace to empty string
					const normalizeText = (text) => {
						if (!text) return "" // null, undefined, or empty string → ""
						return text.trim() === '' ? '' : text
					}

					payload = {
						reminder_1_enabled: reminderData.reminder_1_enabled || false,
						reminder_1_minutes: reminderData.reminder_1_minutes ? Number(reminderData.reminder_1_minutes) : null,
						// Preserve empty strings (merchant cleared), normalize <p></p> to ""
						reminder_1_email_subject: normalizeText(reminderData.reminder_1_email_subject),
						reminder_1_email_body: normalizeEmailBody(reminderData.reminder_1_email_body),
						reminder_1_sms_template: normalizeText(reminderData.reminder_1_sms_template),
						reminder_2_enabled: reminderData.reminder_2_enabled || false,
						reminder_2_minutes: reminderData.reminder_2_minutes ? Number(reminderData.reminder_2_minutes) : null,
						reminder_2_email_subject: normalizeText(reminderData.reminder_2_email_subject),
						reminder_2_email_body: normalizeEmailBody(reminderData.reminder_2_email_body),
						reminder_2_sms_template: normalizeText(reminderData.reminder_2_sms_template),
						reminder_3_enabled: reminderData.reminder_3_enabled || false,
						reminder_3_minutes: reminderData.reminder_3_minutes ? Number(reminderData.reminder_3_minutes) : null,
						reminder_3_email_subject: normalizeText(reminderData.reminder_3_email_subject),
						reminder_3_email_body: normalizeEmailBody(reminderData.reminder_3_email_body),
						reminder_3_sms_template: normalizeText(reminderData.reminder_3_sms_template),
					}
				}
			} else if (activeTab === 'payments') {
				payload = { payment_methods: paymentMethods }
			} else if (activeTab === 'policy') {
				// If no cancellation window is set, nullify the fee type and value
				const hasLateCancelWindow = lateCancelHours !== '' && lateCancelHours !== null
				// If no-show fee type is empty ("Same as late cancellation"), nullify the fee value too
				const hasNoShowFeeType = noShowFeeType !== ''

				payload = {
					late_cancel_hours: hasLateCancelWindow ? Number(lateCancelHours) : null,
					late_cancel_fee_type: hasLateCancelWindow && lateCancelFeeType !== '' ? lateCancelFeeType : null,
					late_cancel_fee_value: hasLateCancelWindow && lateCancelFeeValue !== '' && lateCancelFeeValue !== null ? Number(lateCancelFeeValue) : null,
					no_show_fee_type: hasNoShowFeeType ? noShowFeeType : null,
					no_show_fee_value: hasNoShowFeeType && noShowFeeValue !== '' && noShowFeeValue !== null ? Number(noShowFeeValue) : null,
				}
			}

			const { data, error } = await supabase
				.from('merchant')
				.update(payload)
				.eq('id', merchant.id)
				.select()
				.single()

			if (error) throw error

			setSuccessMessage("Settings saved successfully")
			onSaved && onSaved(data)

			// Advance the payment-methods baseline so the dirty flag clears
			if (activeTab === 'payments') {
				setSavedPaymentMethods(paymentMethods)
			}

			// If on reminders tab, go back to main list
			if (activeTab === 'reminders' && remindersSaveSuccessRef.current) {
				remindersSaveSuccessRef.current()
			}

			// Clear success message after 3 seconds
			setTimeout(() => setSuccessMessage(""), 3000)
		} catch (e) {
			setErrorMessage(e.message || 'Failed to save settings')
		} finally {
			setSaving(false)
		}
	}

	// ── Shared field helpers + panel content used by both the desktop
	// "Settings · workspace" and the mobile "Mobile · settings" renders below;
	// only the surrounding chrome (tabs vs. section screens) differs. ──
	// ── shared field helpers + per-tab panel content (desktop + mobile reuse
	// these; only the surrounding chrome differs). `stacked` = single-column
	// layout for the narrow mobile section screens. ──
	const cs = getCurrencySymbol(merchant?.currency)
	const sel = (value, onChange, items, extra = {}) => (
		<TextField
			select
			fullWidth
			value={value}
			onChange={onChange}
			SelectProps={{ MenuProps: stMenuProps, displayEmpty: true }}
			sx={stInputSx}
			{...extra}
		>
			{items}
		</TextField>
	)
	const inp = (value, onChange, extra = {}) => (
		<TextField fullWidth value={value} onChange={onChange} sx={stInputSx} {...extra} />
	)

	const rowSx = (stacked) => ({
		display: 'flex', flexDirection: stacked ? 'column' : 'row',
		alignItems: stacked ? 'stretch' : 'flex-end', gap: stacked ? '12px' : '10px', flexWrap: 'wrap',
	})

	const renderBusinessContent = (stacked = false) => (
		<>
			<StPanel title="Business profile" subtitle="Shown on your booking page, receipts and client messages.">
				{/* Logo saves straight to merchant_photos (not part of the Save bar). */}
				<BusinessLogoUpload merchant={merchant} stacked={stacked} />
				<Box sx={{ height: '1px', bgcolor: '#EFEBF7', my: '18px' }} />
				<StGrid2 stacked={stacked}>
					<StField label="Store name" required span2={!stacked}>
						{inp(name, (e) => setName(e.target.value), { placeholder: 'e.g. Pawfect Grooming Co.' })}
					</StField>
					<StField label="Timezone">
						{sel(timezone, (e) => setTimezone(e.target.value), TIMEZONES.map((tz) => {
							const offset = tz.rawOffsetInMinutes
							const hours = Math.floor(Math.abs(offset) / 60)
							const mins = Math.abs(offset) % 60
							const sign = offset >= 0 ? '+' : '-'
							const gmtLabel = `GMT${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
							return <MenuItem key={tz.name} value={tz.name}>{tz.name} ({gmtLabel})</MenuItem>
						}))}
					</StField>
					<StField label="Currency">
						{sel(currency, (e) => setCurrency(e.target.value), SUPPORTED_CURRENCIES.map((c) => (
							<MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
						)))}
					</StField>
					<StField label="Phone">
						{inp(phone, (e) => setPhone(e.target.value), { placeholder: 'e.g. (555) 123-4567' })}
					</StField>
					<StField label="Tax rate (%)" hint="e.g. 8.5 for 8.5%. Leave empty for no tax.">
						{inp(taxRate || '', (e) => setTaxRate(e.target.value === '' ? '' : e.target.value), {
							type: 'number', inputProps: { min: 0, max: 100, step: 0.01 }, placeholder: '0',
						})}
					</StField>
				</StGrid2>
			</StPanel>

			<StPanel
				title="Store hours"
				subtitle="Online booking only offers times inside these hours."
				flush
				action={!stacked && (
					<Box sx={{ display: 'flex', gap: '8px' }}>
						<StCountPill strong={hoursPills.days} rest="days open" />
						<StCountPill strong={hoursPills.hrs} rest="hrs / week" />
					</Box>
				)}
			>
				<StoreHoursEditor
					weeklyHours={weeklyHours}
					setWeeklyHours={setWeeklyHours}
					isDayOpen={isDayOpen}
					setDayOpen={setDayOpen}
					stacked={stacked}
				/>
				<Box sx={{ p: stacked ? '2px 14px 14px' : '4px 20px 16px' }}>
					<StNote>
						To close midday, use <strong>+</strong> to add a second set of hours (e.g. 9:00–12:30 and
						1:30–6:00). The gap can&rsquo;t be booked. For one person&rsquo;s lunch or a one-off closure,
						block that time on the calendar instead.
					</StNote>
				</Box>
			</StPanel>

			<StPanel title="Closed dates" subtitle="Recurring dates you're closed every year (e.g. holidays).">
				<Box sx={{ mb: '14px' }}>
					<StField label="Add closed dates" hint="Pick a single day, or a start and end date for a range.">
						{/* Add sits on the same line as the pickers — keeping it a sibling
						    of StField made it align to the field's bottom, which is the
						    hint text, leaving it lower than the inputs.
						    Both calendars open upward: this is the last panel on the page,
						    so a downward popover is clipped by the scroll area. */}
						<Box sx={{
							display: 'flex', alignItems: 'center', gap: '8px',
							flexWrap: stacked ? 'wrap' : 'nowrap',
						}}>
							<Box sx={{ flex: stacked ? 1 : 'none', minWidth: 0, width: stacked ? 'auto' : 190 }}>
								<DropdownCalendar
									up
									value={tempClosedStart ? dayjs(tempClosedStart).format('YYYY-MM-DD') : ''}
									onChange={(v) => {
										const next = v ? dayjs(v) : null
										setTempClosedStart(next)
										// Keep the range valid when the start moves past the end.
										if (next && tempClosedEnd && dayjs(tempClosedEnd).isBefore(next, 'day')) {
											setTempClosedEnd(null)
										}
									}}
									placeholder="Start date"
								/>
							</Box>
							<Box component="span" sx={{ flex: 'none', fontSize: '12.5px', fontWeight: 650, color: L.muted }}>
								to
							</Box>
							<Box sx={{ flex: stacked ? 1 : 'none', minWidth: 0, width: stacked ? 'auto' : 190 }}>
								<DropdownCalendar
									up
									align="right"
									min={tempClosedStart ? dayjs(tempClosedStart).format('YYYY-MM-DD') : undefined}
									disabled={!tempClosedStart}
									value={tempClosedEnd ? dayjs(tempClosedEnd).format('YYYY-MM-DD') : ''}
									onChange={(v) => setTempClosedEnd(v ? dayjs(v) : null)}
									placeholder="End date"
								/>
							</Box>
							<Box
								component="button"
								type="button"
								onClick={addClosedDate}
								disabled={!tempClosedStart}
								sx={{ ...stGhostBtnSx, height: 42, flex: 'none', ...(stacked ? { width: '100%' } : {}) }}
							>
								<StIc name="plus" size={14} sw={2} /><span>Add</span>
							</Box>
						</Box>
					</StField>
				</Box>
				{closedDates.length === 0 ? (
					<Box sx={{ fontSize: '13px', fontWeight: 600, color: L.muted }}>No closed dates yet.</Box>
				) : (
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
						{closedDates.map((d) => (
							<Box key={d} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: L.primarySoft, color: L.primaryDeep, borderRadius: '99px', p: '5px 7px 5px 12px', fontSize: '12.5px', fontWeight: 700 }}>
								<span>{d}</span>
								<Box component="button" type="button" aria-label="Remove date" onClick={() => removeClosedDate(d)} sx={{ width: 20, height: 20, border: 'none', borderRadius: '50%', bgcolor: 'transparent', color: L.primaryDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#E2DAF7' } }}>
									<StIc name="trash" size={12} />
								</Box>
							</Box>
						))}
					</Box>
				)}
			</StPanel>
		</>
	)

	const renderPolicyContent = () => (
		<>
			<StPanel title="Late Cancellation fee (online booking only)" subtitle="Charge for late cancellations. Leave off for free cancellation anytime.">
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: 520 }}>
					<StField label="Late cancellation window">
						{sel(lateCancelHoursCustom ? 'custom' : lateCancelHours, (e) => {
							const v = e.target.value
							if (v === 'custom') { setLateCancelHoursCustom(true); setLateCancelHours('') }
							else if (v === '') { setLateCancelHoursCustom(false); setLateCancelHours(''); setLateCancelFeeType(''); setLateCancelFeeValue(''); setLateCancelFeeCustom(false) }
							else { setLateCancelHoursCustom(false); setLateCancelHours(v) }
						}, [
							<MenuItem key="none" value="">None (free cancellation anytime)</MenuItem>,
							<MenuItem key="24" value="24">24 hours</MenuItem>,
							<MenuItem key="48" value="48">48 hours</MenuItem>,
							<MenuItem key="72" value="72">72 hours</MenuItem>,
							<MenuItem key="custom" value="custom">Custom</MenuItem>,
						])}
					</StField>
					{lateCancelHoursCustom && (
						<StField label="Custom hours" hint="Enter hours (1–120).">
							{inp(lateCancelHours || '', (e) => setLateCancelHours(e.target.value), { type: 'number', inputProps: { min: 1, max: 120 } })}
						</StField>
					)}
					{lateCancelHours && lateCancelHours !== '' && (
						<>
							<StField label="Fee type">
								{sel(lateCancelFeeType, (e) => { setLateCancelFeeType(e.target.value); setLateCancelFeeValue(''); setLateCancelFeeCustom(false) }, [
									<MenuItem key="p" value="percentage">% of service price</MenuItem>,
									<MenuItem key="f" value="fixed">Flat fee</MenuItem>,
								])}
							</StField>
							{lateCancelFeeType === 'percentage' && (
								<>
									<StField label="Percentage">
										{sel(lateCancelFeeCustom ? 'custom' : lateCancelFeeValue, (e) => {
											if (e.target.value === 'custom') { setLateCancelFeeCustom(true); setLateCancelFeeValue('') }
											else { setLateCancelFeeCustom(false); setLateCancelFeeValue(e.target.value) }
										}, [
											<MenuItem key="25" value="25">25%</MenuItem>,
											<MenuItem key="50" value="50">50%</MenuItem>,
											<MenuItem key="custom" value="custom">Custom</MenuItem>,
										])}
									</StField>
									{lateCancelFeeCustom && (
										<StField label="Custom percentage" hint="Enter percentage (1–100).">
											{inp(lateCancelFeeValue, (e) => setLateCancelFeeValue(e.target.value), { type: 'number', inputProps: { min: 1, max: 100 } })}
										</StField>
									)}
								</>
							)}
							{lateCancelFeeType === 'fixed' && (
								<>
									<StField label="Flat fee">
										{sel(lateCancelFeeCustom ? 'custom' : lateCancelFeeValue, (e) => {
											if (e.target.value === 'custom') { setLateCancelFeeCustom(true); setLateCancelFeeValue('') }
											else { setLateCancelFeeCustom(false); setLateCancelFeeValue(e.target.value) }
										}, [
											<MenuItem key="1000" value="1000">{cs}10</MenuItem>,
											<MenuItem key="2500" value="2500">{cs}25</MenuItem>,
											<MenuItem key="custom" value="custom">Custom</MenuItem>,
										])}
									</StField>
									{lateCancelFeeCustom && (
										<StField label="Custom amount" hint="Enter amount in dollars (e.g. 20.00).">
											{inp(lateCancelFeeValue ? lateCancelFeeValue / 100 : '', (e) => setLateCancelFeeValue(e.target.value ? Math.round(parseFloat(e.target.value) * 100) : ''), { type: 'number', inputProps: { min: 0.01, step: 0.01 } })}
										</StField>
									)}
								</>
							)}
						</>
					)}
				</Box>
			</StPanel>

			<StPanel title="No-show fee (online booking only)" subtitle="Charge when a client doesn't show. Leave as “same as late cancellation” to reuse the fee above.">
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: 520 }}>
					<StField label="Fee type">
						{sel(noShowFeeType, (e) => { setNoShowFeeType(e.target.value); setNoShowFeeValue(''); setNoShowFeeCustom(false) }, [
							<MenuItem key="same" value="">Same as late cancellation</MenuItem>,
							<MenuItem key="p" value="percentage">% of service price</MenuItem>,
							<MenuItem key="f" value="fixed">Flat fee</MenuItem>,
						])}
					</StField>
					{noShowFeeType === 'percentage' && (
						<>
							<StField label="Percentage">
								{sel(noShowFeeCustom ? 'custom' : noShowFeeValue, (e) => {
									if (e.target.value === 'custom') { setNoShowFeeCustom(true); setNoShowFeeValue('') }
									else { setNoShowFeeCustom(false); setNoShowFeeValue(e.target.value) }
								}, [
									<MenuItem key="50" value="50">50%</MenuItem>,
									<MenuItem key="100" value="100">100%</MenuItem>,
									<MenuItem key="custom" value="custom">Custom</MenuItem>,
								])}
							</StField>
							{noShowFeeCustom && (
								<StField label="Custom percentage" hint="Enter percentage (1–100).">
									{inp(noShowFeeValue, (e) => setNoShowFeeValue(e.target.value), { type: 'number', inputProps: { min: 1, max: 100 } })}
								</StField>
							)}
						</>
					)}
					{noShowFeeType === 'fixed' && (
						<>
							<StField label="Flat fee">
								{sel(noShowFeeCustom ? 'custom' : noShowFeeValue, (e) => {
									if (e.target.value === 'custom') { setNoShowFeeCustom(true); setNoShowFeeValue('') }
									else { setNoShowFeeCustom(false); setNoShowFeeValue(e.target.value) }
								}, [
									<MenuItem key="2500" value="2500">{cs}25</MenuItem>,
									<MenuItem key="5000" value="5000">{cs}50</MenuItem>,
									<MenuItem key="custom" value="custom">Custom</MenuItem>,
								])}
							</StField>
							{noShowFeeCustom && (
								<StField label="Custom amount" hint="Enter amount in dollars (e.g. 50.00).">
									{inp(noShowFeeValue ? noShowFeeValue / 100 : '', (e) => setNoShowFeeValue(e.target.value ? Math.round(parseFloat(e.target.value) * 100) : ''), { type: 'number', inputProps: { min: 0.01, step: 0.01 } })}
								</StField>
							)}
						</>
					)}
				</Box>
				{(lateCancelHours || noShowFeeType) && (
					<StNote variant="info">
						<Box sx={{ fontWeight: 750, mb: '3px' }}>Policy preview</Box>
						{lateCancelHours && lateCancelFeeType && lateCancelFeeValue && (
							<Box>• Cancellations within {lateCancelHours} hours: {lateCancelFeeType === 'percentage' ? `${lateCancelFeeValue}% of service price` : `${cs}${(lateCancelFeeValue / 100).toFixed(2)}`}</Box>
						)}
						{noShowFeeType && noShowFeeValue ? (
							<Box>• No-show: {noShowFeeType === 'percentage' ? `${noShowFeeValue}% of service price` : `${cs}${(noShowFeeValue / 100).toFixed(2)}`}</Box>
						) : lateCancelHours && lateCancelFeeType && lateCancelFeeValue && (
							<Box>• No-show: same as late cancellation ({lateCancelFeeType === 'percentage' ? `${lateCancelFeeValue}% of service price` : `${cs}${(lateCancelFeeValue / 100).toFixed(2)}`})</Box>
						)}
					</StNote>
				)}
			</StPanel>
		</>
	)

	const renderPaymentsContent = (stacked = false) => (
		<>
			<StPanel title="Payment methods" subtitle="Choose which methods appear when logging a payment." flush>
				<Box sx={{ p: '12px', display: 'grid', gridTemplateColumns: stacked ? '1fr' : '1fr 1fr', gap: '8px' }}>
					{ALL_PAYMENT_METHODS.filter((method) => {
						if (method.value === 'square_terminal') return isSquareConnected && Boolean(merchant?.square_terminal_device_id)
						if (method.value === 'square_card') return isSquareConnected
						return true
					}).map((method) => {
						const enabled = paymentMethods.includes(method.value)
						return (
							<Box
								key={method.value}
								onClick={() => setPaymentMethods((prev) => enabled ? prev.filter((v) => v !== method.value) : [...prev, method.value])}
								sx={{
									display: 'flex', alignItems: 'center', gap: '11px', p: '12px 13px', borderRadius: '11px',
									border: `1.5px solid ${enabled ? '#D8CEF2' : '#EAE6F3'}`, bgcolor: enabled ? '#F7F5FC' : '#FFFFFF',
									cursor: 'pointer', userSelect: 'none', minWidth: 0,
									'&:hover': { borderColor: '#D8CEF2' },
								}}
							>
								<Box sx={{ width: 30, height: 30, flex: 'none', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: enabled ? L.primarySoft : L.surface, color: enabled ? L.primaryDeep : L.muted }}>
									<StIc name="card" size={15} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 700, color: enabled ? L.primaryDeep : L.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{method.label}
								</Box>
								<Box sx={{ width: 20, height: 20, flex: 'none', borderRadius: '50%', border: `2px solid ${enabled ? L.primary : '#CBD5E0'}`, bgcolor: enabled ? L.primary : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
									{enabled && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FFFFFF' }} />}
								</Box>
							</Box>
						)
					})}
				</Box>
				{paymentMethods.length === 0 && (
					<Box sx={{ px: '20px', pb: '16px' }}>
						<StNote variant="warn">At least one payment method must be selected.</StNote>
					</Box>
				)}
			</StPanel>

			<StPanel title="Square Terminal" subtitle="Accept in-person card payments via a paired Square Terminal device.">
				{isSquareConnected ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
							<StStatusPill ok>Connected</StStatusPill>
							{merchant?.square_merchant_id && (
								<Box sx={{ fontSize: '12.5px', color: L.muted, fontWeight: 600 }}>Merchant ID: <b style={{ color: L.text }}>{merchant.square_merchant_id}</b></Box>
							)}
						</Box>
						{(merchant?.square_location_id || merchant?.square_token_expires_at) && (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12.5px', color: L.muted, fontWeight: 600 }}>
								{merchant?.square_location_id && <Box>Location ID: <b style={{ color: L.text }}>{merchant.square_location_id}</b></Box>}
								{merchant?.square_token_expires_at && <Box>Token expires: {new Date(merchant.square_token_expires_at).toLocaleDateString()}</Box>}
							</Box>
						)}

						<Box sx={{ borderTop: '1px solid #EFEBF7', pt: '14px' }}>
							<Box sx={{ fontSize: '13px', fontWeight: 750, mb: '10px' }}>Terminal device</Box>
							{!showDevicePicker ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-start' }}>
									{merchant?.square_terminal_device_id ? (
										<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '12px 14px', borderRadius: '10px', border: `1px solid ${L.border}`, bgcolor: L.surface }}>
											<Box sx={{ minWidth: 0 }}>
												<Box sx={{ fontSize: '13px', fontWeight: 700 }}>Terminal connected</Box>
												<Box sx={{ fontSize: '11.5px', color: L.muted, mt: '2px', fontWeight: 600 }}>ID: {merchant.square_terminal_device_id}</Box>
											</Box>
											<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2F8A5B', flexShrink: 0 }} />
										</Box>
									) : (
										<Box sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>No terminal device selected.</Box>
									)}
									{terminalDeviceSaved && <Box sx={{ fontSize: '13px', color: '#2F8A5B', fontWeight: 650 }}>✓ Device saved</Box>}
									{/* Sandbox device codes can never be paired to a Terminal, so dev skips the pairing step
									    and relies on the simulated devices that sandbox ListDevices returns. */}
									{isDev || pairingStatus === 'idle' || pairingStatus === 'paired' ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
											{!isDev && (
												<Box component="button" type="button" onClick={handleGeneratePairingCode} sx={stPrimaryBtnSx}>
													<span>{merchant?.square_terminal_device_id ? 'Pair another Terminal' : 'Pair a Terminal'}</span>
												</Box>
											)}
											<Box component="button" type="button" onClick={handleOpenDevicePicker} sx={stGhostBtnSx}>
												{merchant?.square_terminal_device_id ? 'Change device' : 'Select device'}
											</Box>
										</Box>
									) : (
										<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', p: '14px 16px', borderRadius: '10px', border: `1px solid ${L.border}`, bgcolor: L.surface }}>
											{pairingStatus === 'creating' && (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<CircularProgress size={14} sx={{ color: L.primary }} />
													<Box sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>Creating pairing code…</Box>
												</Box>
											)}
											{(pairingStatus === 'waiting' || pairingStatus === 'saving') && pairing && (
												<>
													<Box sx={{ fontSize: '13px', fontWeight: 750 }}>Enter this code on your Square Terminal</Box>
													<Box sx={{ fontSize: '30px', fontWeight: 800, letterSpacing: '0.18em', color: L.primaryDeep, fontVariantNumeric: 'tabular-nums' }}>{pairing.code}</Box>
													<Box sx={{ fontSize: '12px', color: L.muted, fontWeight: 600, lineHeight: 1.55 }}>
														On the Terminal, sign out if needed, then tap <b style={{ color: L.text }}>Sign in</b> → <b style={{ color: L.text }}>Use a device code</b> and enter the code above.
														{pairing.pairBy && <> Code expires at {new Date(pairing.pairBy).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.</>}
													</Box>
													<Box sx={{ fontSize: '12px', color: L.muted, fontWeight: 600, lineHeight: 1.55 }}>
														Pairing switches the Terminal into Terminal API mode, so it will no longer run Square POS or Appointments while paired to this app.
													</Box>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
														<CircularProgress size={14} sx={{ color: L.primary }} />
														<Box sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>
															{pairingStatus === 'saving' ? 'Terminal paired — saving device…' : 'Waiting for the Terminal to sign in…'}
														</Box>
													</Box>
												</>
											)}
											{pairingStatus === 'expired' && (
												<Box sx={{ fontSize: '13px', color: '#BE4B45', fontWeight: 600 }}>The pairing code expired before the Terminal signed in. Generate a new one and enter it within 5 minutes.</Box>
											)}
											{pairingStatus === 'error' && (
												<Box sx={{ fontSize: '13px', color: '#BE4B45', fontWeight: 600 }}>{pairingError}</Box>
											)}
											<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
												{(pairingStatus === 'expired' || pairingStatus === 'error') && (
													<Box component="button" type="button" onClick={handleGeneratePairingCode} sx={stPrimaryBtnSx}>
														<span>Generate new code</span>
													</Box>
												)}
												<Box component="button" type="button" onClick={handleCancelPairing} disabled={pairingStatus === 'saving'} sx={{ ...stGhostBtnSx, border: 'none', color: L.muted }}>
													{pairingStatus === 'expired' || pairingStatus === 'error' ? 'Close' : 'Cancel'}
												</Box>
											</Box>
										</Box>
									)}
								</Box>
							) : (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
									<Box sx={{ fontSize: '12px', color: L.muted, fontWeight: 600, lineHeight: 1.5 }}>
										{isDev
											? 'Select the sandbox Square Terminal that will receive payment requests.'
											: 'Select the Square Terminal that will receive payment requests. Only Terminals paired to this app with a pairing code appear here.'}
									</Box>
									{squareDevicesError && <Box sx={{ fontSize: '12px', color: '#BE4B45', fontWeight: 600 }}>{squareDevicesError}</Box>}
									{squareDevicesLoading ? (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
											<CircularProgress size={14} sx={{ color: L.primary }} />
											<Box sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>Loading devices…</Box>
										</Box>
									) : squareDevices.length === 0 ? (
										<Box sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>
											{isDev
												? 'No sandbox terminal devices found.'
												: 'No paired Terminals found. A Terminal signed in with your regular Square login won\'t appear here — cancel and use "Pair a Terminal" to generate a pairing code.'}
										</Box>
									) : (
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
											{squareDevices.map((device) => {
												const isSelected = selectedTerminalDeviceId === device.id
												const isAvailable = device.status === 'AVAILABLE' || device.status === 'PAIRED'
												return (
													<Box key={device.id} onClick={() => setSelectedTerminalDeviceId(device.id)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', p: '12px 14px', borderRadius: '10px', border: `1.5px solid ${isSelected ? L.primary : '#EAE6F3'}`, bgcolor: isSelected ? '#F7F5FC' : '#FFFFFF', cursor: 'pointer', '&:hover': { borderColor: L.primary } }}>
														<Box sx={{ minWidth: 0 }}>
															<Box sx={{ fontSize: '13px', fontWeight: 700, color: L.text }}>{device.name}</Box>
															<Box sx={{ fontSize: '11.5px', color: L.muted, fontWeight: 600 }}>ID: {device.id}</Box>
														</Box>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
															<Box sx={{ px: '8px', py: '2px', borderRadius: '12px', bgcolor: isAvailable ? '#E9F6EE' : '#FBECEB', color: isAvailable ? '#25714A' : '#BE4B45', fontSize: '11px', fontWeight: 700 }}>{device.status}</Box>
															{isSelected && (
																<Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: L.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
																	<StIc name="check" size={11} sw={2.2} />
																</Box>
															)}
														</Box>
													</Box>
												)
											})}
										</Box>
									)}
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<Box component="button" type="button" onClick={handleSaveTerminalDevice} disabled={savingTerminalDevice || !selectedTerminalDeviceId} sx={stPrimaryBtnSx}>
											{savingTerminalDevice && <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />}
											<span>Save device</span>
										</Box>
										<Box component="button" type="button" onClick={handleCancelDevicePicker} disabled={savingTerminalDevice} sx={{ ...stGhostBtnSx, border: 'none', color: L.muted }}>Cancel</Box>
										<Box component="button" type="button" onClick={loadSquareDevices} disabled={squareDevicesLoading} sx={{ ...stGhostBtnSx, border: 'none', color: L.primaryDeep, ml: 'auto' }}>Refresh</Box>
									</Box>
								</Box>
							)}
						</Box>

						<Box component="button" type="button" onClick={handleDisconnectSquare} disabled={squareDisconnecting} sx={{ ...stDangerBtnSx, alignSelf: 'flex-start' }}>
							{squareDisconnecting ? <CircularProgress size={16} sx={{ color: '#BE4B45' }} /> : 'Disconnect Square'}
						</Box>
					</Box>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'flex-start' }}>
						<StStatusPill>Not connected</StStatusPill>
						<Box component="button" type="button" onClick={handleConnectSquare} sx={stPrimaryBtnSx}>
							<StIc name="link" size={15} /><span>Connect Square account</span>
						</Box>
						<Box sx={{ fontSize: '12.5px', color: L.muted, fontWeight: 600, lineHeight: 1.5 }}>
							You'll be redirected to Square to authorize access. After approving, you'll be returned here automatically.
						</Box>
					</Box>
				)}
			</StPanel>
		</>
	)

	const renderMessagingContent = (stacked = false) => (
		<StPanel title="Messaging" subtitle="Rent a dedicated number to text your clients and receive replies. ~$1.15 USD/month, billed to your Twilio account.">
			{messagingError && <Alert severity="error" sx={{ mb: 1.5 }}>{messagingError}</Alert>}
			{messagingSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{messagingSuccess}</Alert>}
			{isMessagingEnabled ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
						<StStatusPill ok>Active</StStatusPill>
						<Box sx={{ fontSize: '14px', fontWeight: 700 }}>{merchant.twilio_phone_number}</Box>
						{merchant?.twilio_phone_provisioned_at && (
							<Box sx={{ fontSize: '12.5px', color: L.muted, fontWeight: 600 }}>since {new Date(merchant.twilio_phone_provisioned_at).toLocaleDateString()}</Box>
						)}
					</Box>
					<Box sx={{ fontSize: '12px', color: L.muted, fontWeight: 600 }}>
						Newly purchased numbers may take up to 15 minutes to be fully provisioned.
					</Box>
					{canReleaseMessagingNumber && (
						<Box component="button" type="button" onClick={handleReleaseTwilioNumber} disabled={messagingReleasing} sx={stDangerBtnSx}>
							{messagingReleasing ? <CircularProgress size={16} sx={{ color: '#BE4B45' }} /> : 'Release number'}
						</Box>
					)}
				</Box>
			) : !canPurchaseMessagingNumber ? (
				<StNote variant="info">Messaging is not available for your account. Please email kevin@nami.pet to request access.</StNote>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<Box sx={{ ...rowSx(stacked), gap: '10px' }}>
						<StField label="Area code (optional)">
							<Box sx={{ width: stacked ? '100%' : 160 }}>
								{inp(messagingAreaCode, (e) => setMessagingAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3)), {
									placeholder: '3 digits', disabled: messagingSearching || messagingProvisioning, inputProps: { maxLength: 3, inputMode: 'numeric' },
								})}
							</Box>
						</StField>
						<Box component="button" type="button" onClick={handleSearchTwilioNumbers} disabled={messagingSearching || messagingProvisioning} sx={{ ...stGhostBtnSx, height: 40, borderColor: L.primary, color: L.primaryDeep, ...(stacked ? { width: '100%' } : {}) }}>
							{messagingSearching ? <><CircularProgress size={15} sx={{ color: L.primaryDeep }} /><span>Searching…</span></> : <span>{messagingHasSearched ? 'Refresh' : 'Search numbers'}</span>}
						</Box>
					</Box>

					{messagingHasSearched && !messagingSearching && messagingAvailableNumbers.length === 0 && (
						<Box sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>
							No SMS-capable numbers were found{messagingAreaCode ? ` for area code ${messagingAreaCode}` : ''}. Try a different area code.
						</Box>
					)}

					{messagingAvailableNumbers.length > 0 && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: 340, overflowY: 'auto', border: `1px solid ${L.border}`, borderRadius: '11px', p: '8px' }}>
							{messagingAvailableNumbers.map((n) => {
								const isSelected = selectedMessagingNumber === n.phoneNumber
								const subtitle = [n.locality, n.region].filter(Boolean).join(', ')
								return (
									<Box key={n.phoneNumber} onClick={() => !messagingProvisioning && setSelectedMessagingNumber(n.phoneNumber)} sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '11px 13px', borderRadius: '10px', cursor: messagingProvisioning ? 'not-allowed' : 'pointer', border: `1.5px solid ${isSelected ? L.primary : '#EAE6F3'}`, bgcolor: isSelected ? '#F7F5FC' : '#FFFFFF', '&:hover': { borderColor: messagingProvisioning ? '#EAE6F3' : '#D8CEF2' } }}>
										<Box sx={{ width: 19, height: 19, flex: 'none', borderRadius: '50%', border: isSelected ? `6px solid ${L.primary}` : `1.5px solid #D4CDE6`, bgcolor: '#FFFFFF' }} />
										<Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
											<Box sx={{ fontSize: '14px', fontWeight: 750, color: isSelected ? L.primaryDeep : L.text }}>{n.friendlyName || n.phoneNumber}</Box>
											{subtitle && <Box sx={{ fontSize: '12px', color: L.muted, fontWeight: 600 }}>{subtitle}</Box>}
										</Box>
									</Box>
								)
							})}
						</Box>
					)}

					{messagingAvailableNumbers.length > 0 && (
						<Box component="button" type="button" onClick={handlePurchaseSelectedNumber} disabled={!selectedMessagingNumber || messagingProvisioning} sx={{ ...stPrimaryBtnSx, alignSelf: 'flex-start' }}>
							{messagingProvisioning ? <><CircularProgress size={15} sx={{ color: '#FFFFFF' }} /><span>Purchasing…</span></> : <span>{selectedMessagingNumber ? `Purchase ${selectedMessagingNumber}` : 'Select a number'}</span>}
						</Box>
					)}
				</Box>
			)}
		</StPanel>
	)

	if (!isMobile) {
		const desktopTab = ST_TABS.some(t => t.id === activeTab) ? activeTab : 'store'
		const saveDisabled = !name.trim() || (desktopTab === 'payments' && paymentMethods.length === 0)

		// storeDirty / policyDirty are computed once at the top of the component
		// (they also drive the navigate-away warning); reuse them here.
		const tabDirty = {
			store: storeDirty,
			policy: policyDirty,
			payments: paymentMethodsHasUnsaved,
			reminders: Boolean(remindersHasUnsavedChangesRef.current),
		}
		const showSaveBar = Boolean(tabDirty[desktopTab])

		return (
			<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: '#FBFAFE', borderRadius: '12px', fontFamily: L.font, color: L.text, overflow: 'hidden' }}>
				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable', p: '22px 26px 26px' }}>
					{/* Header */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mb: '18px' }}>
						<Box component="h1" sx={{ fontSize: '23px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>Settings</Box>
						<StTabs tabs={ST_TABS} active={desktopTab} onChange={handleTabChange} />
					</Box>

					{successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
					{errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						{/* Each tab reuses the shared panel-content functions defined above. */}
						{desktopTab === 'store' && renderBusinessContent(false)}
						{desktopTab === 'reminders' && (
							<SettingsReminders
								merchant={merchant}
								onChange={setReminderData}
								onCancelRef={remindersCancelRef}
								hasUnsavedChangesRef={remindersHasUnsavedChangesRef}
								textingActive={Boolean(merchant?.twilio_phone_number)}
								onGoMessaging={() => handleTabChange('messaging')}
							/>
						)}
						{desktopTab === 'policy' && renderPolicyContent()}
						{desktopTab === 'payments' && renderPaymentsContent(false)}
						{desktopTab === 'messaging' && renderMessagingContent(false)}
					</Box>

					{showSaveBar && (
						<StSaveBar
							saving={saving}
							saveDisabled={saveDisabled}
							onSave={handleSave}
							onDiscard={discardChanges}
						/>
					)}
				</Box>
			</Box>
		)
	}

	// ── mobile navigation + per-section summaries (home menu) ──
	const currentTabDirty = () => ({
		store: storeDirty,
		policy: policyDirty,
		payments: paymentMethodsHasUnsaved,
		reminders: Boolean(remindersHasUnsavedChangesRef.current),
	}[activeTab])

	const mobileSummary = (id) => {
		if (id === 'store') {
			const openN = dayLabels.filter((_, i) => (weeklyHours[String(i)] || []).length > 0).length
			return `${name || 'Your business'} · open ${openN} ${openN === 1 ? 'day' : 'days'}/wk`
		}
		if (id === 'reminders') {
			const n = [1, 2, 3].filter((i) => merchant?.[`reminder_${i}_enabled`]).length
			return n ? `${n} of 3 reminders on` : 'No reminders scheduled'
		}
		if (id === 'policy') {
			return lateCancelHours ? `${lateCancelHours}h cancellation window` : 'No cancellation fee'
		}
		if (id === 'payments') {
			return `${paymentMethods.length} ${paymentMethods.length === 1 ? 'method' : 'methods'} · Square ${isSquareConnected ? 'connected' : 'not connected'}`
		}
		return isMessagingEnabled ? `Texting from ${merchant.twilio_phone_number}` : 'Texting number not set up'
	}

	const openMobileSection = (id) => { setActiveTab(id); setMobileScreen('section') }
	const goMobileHome = () => {
		if (currentTabDirty()) {
			if (!window.confirm('You have unsaved changes. Are you sure you want to leave? Your changes will be lost.')) return
			discardChanges()
		}
		setMobileScreen('home')
	}

	const mobileTab = ST_TABS.find((t) => t.id === activeTab) || ST_TABS[0]
	const mobileSaveDisabled = !name.trim() || (activeTab === 'payments' && paymentMethods.length === 0)

	// Mobile "Mobile · settings": a section home menu + full-screen section pages.
	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: L.bg, color: L.text, fontFamily: L.font, overflow: 'hidden' }}>
			{mobileScreen === 'home' ? (
				<>
					<Box sx={{ flex: 'none', p: '20px 16px 14px', bgcolor: L.bg, borderBottom: `1px solid ${L.border}` }}>
						<Box component="h1" sx={{ fontSize: '30px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>Settings</Box>
						<Box sx={{ mt: '3px', fontSize: '13px', fontWeight: 600, color: L.muted }}>
							Business preferences{name ? ` for ${name}` : ''}
						</Box>
					</Box>
					<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '14px 16px 24px' }}>
						{successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
						{errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
							{ST_TABS.map((t) => (
								<Box
									component="button"
									type="button"
									key={t.id}
									onClick={() => openMobileSection(t.id)}
									sx={{
										display: 'flex', alignItems: 'center', gap: '13px', width: '100%', textAlign: 'left',
										p: '15px 14px', bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '16px',
										boxShadow: '0 1px 3px rgba(46,42,64,0.04)', cursor: 'pointer', fontFamily: L.font,
										'&:active': { bgcolor: '#FBFAFE' },
									}}
								>
									<Box sx={{ width: 44, height: 44, flex: 'none', borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: L.primarySoft, color: L.primaryDeep }}>
										<StIc name={t.icon} size={18} sw={1.5} />
									</Box>
									<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
										<Box component="strong" sx={{ fontSize: '15.5px', fontWeight: 750, letterSpacing: '-0.01em', color: L.text }}>{t.label}</Box>
										<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
											{mobileSummary(t.id)}
										</Box>
									</Box>
									<Box sx={{ display: 'flex', color: '#C4BDD6', flex: 'none' }}><StIc name="chevright" size={14} sw={1.8} /></Box>
								</Box>
							))}
						</Box>
					</Box>
				</>
			) : (
				<>
					<Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '11px', p: '12px 16px 10px', bgcolor: L.bg, borderBottom: `1px solid ${L.border}` }}>
						<Box
							component="button"
							type="button"
							aria-label="Back to settings"
							onClick={goMobileHome}
							sx={{ width: 40, height: 40, flex: 'none', ml: '-8px', border: 'none', borderRadius: '11px', bgcolor: 'transparent', color: L.text, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: L.font, '&:active': { bgcolor: L.surface } }}
						>
							<StIc name="back" size={17} sw={1.8} />
						</Box>
						<Box component="strong" sx={{ flex: 1, minWidth: 0, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.015em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
							{mobileTab.label}
						</Box>
						{currentTabDirty() && (
							<Box
								component="button"
								type="button"
								onClick={handleSave}
								disabled={saving || mobileSaveDisabled}
								sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 38, px: '15px', border: 'none', borderRadius: '11px', bgcolor: L.primary, color: L.onPrimary, fontSize: '13.5px', fontWeight: 700, boxShadow: '0 2px 8px rgba(124,107,217,0.28)', cursor: 'pointer', fontFamily: L.font, '&:active': { bgcolor: L.primaryDeep }, '&:disabled': { opacity: 0.6, cursor: 'default' } }}
							>
								{saving ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : <StIc name="check" size={14} sw={2.2} />}
								<span>Save</span>
							</Box>
						)}
					</Box>
					<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '16px' }}>
						{successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
						{errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
							{activeTab === 'store' && renderBusinessContent(true)}
							{activeTab === 'reminders' && (
								<SettingsReminders
									merchant={merchant}
									onChange={setReminderData}
									onCancelRef={remindersCancelRef}
									hasUnsavedChangesRef={remindersHasUnsavedChangesRef}
									textingActive={Boolean(merchant?.twilio_phone_number)}
									onGoMessaging={() => openMobileSection('messaging')}
								/>
							)}
							{activeTab === 'policy' && renderPolicyContent()}
							{activeTab === 'payments' && renderPaymentsContent(true)}
							{activeTab === 'messaging' && renderMessagingContent(true)}
						</Box>
					</Box>
				</>
			)}
		</Box>
	)
}

